import config from "environment";
import { getRequest, postRequest } from "./client";
import { ConnectedApp, LinkAppConfig } from "winslow-core";

const { baseApi } = config;

export type SlackMessagePayload = {};

export async function apiSlackAuth(
  code: string,
  userId: string,
  workspaceId: string
) {
  console.log("[OAuthService] apiSlackAuth called", {
    userId,
    workspaceId,
    hasCode: !!code,
    codePreview: code ? code.substring(0, 10) + "..." : null,
    url: `${baseApi}/slack/auth`,
    timestamp: new Date().toISOString(),
  });

  const fetchURL = `${baseApi}/slack/auth`;
  const payload = { code, userId, workspaceId };

  try {
    const result = await postRequest<
      { userId: string; workspaceId: string; code: string },
      boolean
    >(fetchURL, payload, "SlackService/apiSlackAuth");

    console.log("[OAuthService] apiSlackAuth result:", {
      result,
      userId,
      workspaceId,
      timestamp: new Date().toISOString(),
    });

    return result;
  } catch (error) {
    console.error("[OAuthService] apiSlackAuth error:", {
      error: error instanceof Error ? error.message : String(error),
      userId,
      workspaceId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
}

export async function apiConfirmOAuth(
  code: string,
  linkAppId: string,
  userId: string
) {
  const fetchURL = `${baseApi}/connected-app/confirm`;
  const payload = { code, linkAppId, userId };
  return await postRequest<{ linkAppId: string; code: string }, boolean>(
    fetchURL,
    payload,
    "SlackService/apiSlackAuth"
  );
}

export async function apiRevokeSlack(userId: string, workspaceId: string) {
  console.log("[OAuthService] apiRevokeSlack called", {
    userId,
    workspaceId,
    url: `${baseApi}/slack/revoke/${userId}/${workspaceId}`,
    timestamp: new Date().toISOString(),
  });

  const fetchURL = `${baseApi}/slack/revoke/${userId}/${workspaceId}`;

  try {
    const result = await getRequest<boolean>(
      fetchURL,
      "OAuthService/apiRevokeSlack"
    );

    console.log("[OAuthService] apiRevokeSlack result:", {
      result,
      userId,
      workspaceId,
      timestamp: new Date().toISOString(),
    });

    return result;
  } catch (error) {
    console.error("[OAuthService] apiRevokeSlack error:", {
      error: error instanceof Error ? error.message : String(error),
      userId,
      workspaceId,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
}

export async function apiRemoveGoogleToken(userId: string) {
  const fetchURL = `${baseApi}/connected-app/revoke/${userId}/GOOGLE`;
  return await getRequest<boolean>(
    fetchURL,
    "OAuthService/apiRemoveGoogleToken"
  );
}

export async function apiRemoveMSToken(userId: string) {
  const fetchURL = `${baseApi}/connected-app/revoke/${userId}/MICROSOFT`;
  return await getRequest<boolean>(fetchURL, "OAuthService/apiRemoveMSToken");
}

export async function apiRemoveFigmaToken(userId: string) {
  const fetchURL = `${baseApi}/connected-app/revoke/${userId}/FIGMA`;
  return await getRequest<boolean>(
    fetchURL,
    "OAuthService/apiRemoveFigmaToken"
  );
}

export async function apiRemoveConfluenceToken(userId: string) {
  const fetchURL = `${baseApi}/connected-app/revoke/${userId}/CONFLUENCE`;
  return await getRequest<boolean>(
    fetchURL,
    "OAuthService/apiRemoveConfluenceToken"
  );
}

export async function apiSaveGoogleToken(
  userId: string,
  slug: { accessToken: string; expiresIn: string }
) {
  const fetchURL = `${baseApi}/connected-app/save-google-token`;
  return await postRequest<
    { userId: string; slug: { accessToken: string; expiresIn: string } },
    boolean
  >(
    fetchURL,
    {
      userId,
      slug,
    },
    `API/apiSaveGoogleToken`
  );
}

export async function apiVerifyGoogleAuth(userId: string, payload: any) {
  const fetchURL = `${baseApi}/connected-app/verify-google-auth`;
  return await postRequest<{ userId: string; payload: any }, boolean>(
    fetchURL,
    {
      userId,
      payload,
    },
    `API/apiVerifyGoogleAuth`
  );
}

export async function apiVerifyMSAuth(userId: string, payload: any) {
  const fetchURL = `${baseApi}/connected-app/verify-ms-auth`;
  return await postRequest<{ userId: string; payload: any }, boolean>(
    fetchURL,
    {
      userId,
      payload,
    },
    `API/apiVerifyMSAuth`
  );
}

export async function apiGetConnectedApps(userId: string) {
  const fetchURL = `${baseApi}/connected-app/${userId}`;
  return await getRequest<ConnectedApp[]>(
    fetchURL,
    "OAuthService/apiGetUserConnectedApps"
  );
}

export async function apiGetSupportedConnectedApps() {
  const fetchURL = `${baseApi}/connected-app/supported`;
  return await getRequest<LinkAppConfig[]>(
    fetchURL,
    "OAuthService/apiGetSupportedConnectedApps"
  );
}

export async function apiGetFirstConnectedAppUrl(
  userId: string
): Promise<string | null> {
  const connectedApps = await apiGetConnectedApps(userId);

  if (!connectedApps) {
    return null;
  }

  for (const app of connectedApps) {
    if (app.slug?.url) {
      return app.slug.url;
    }
  }

  return null;
}
